<template>
  <InternalOrderCancerView icd="C18_C19_C20" icd-presentation-string="C18, C19, or C20"
                           icd-description="De novo metastatic colorectal cancer"
                           disease-description="de novo metastatic colorectal cancer"
                           :mesh-ids="['D015179']"
                           mesh-description="Colorectal Neoplasms"
                           :project-description="projectDescription">
  </InternalOrderCancerView>
</template>

<script>
import InternalOrderCancerView from '@/components/InternalOrderCancerView.vue';
import {defineComponent} from "vue";

export default defineComponent({
  name: 'de-novo-metastatic-colorectal-cancer-page',
  components: {
    InternalOrderCancerView
  },
  data: () => ({
    projectDescription: "<h3>Executive Summary</h3>" +
        "<br/>" +
        "<p>Ciencia Research has been assigned to execute a series of Virtual Expert Advisory Panels (VEAP) to " +
        "support J&J’s ongoing global business development activities in the field of colorectal cancer (CRC). " +
        "Through engagement with up to eight experts, the project aims to evaluate potential new oncology assets for " +
        "acquisition or licensing.</p>" +
        "<br/>" +
        "<p>In addition, the J&J team has an interest in real-world insights on the disease landscape and patients’ " +
        "unmet needs. By leveraging secondary healthcare data from Swedish national registers, Ciencia Research " +
        "explored clinical characteristics, care patterns, and outcomes in patients with metastatic CRC (mCRC) " +
        "patients over the last 15 years in Sweden. The aim is to visualize high-level epidemiological and care " +
        "trends among patients with mCRC on an interactive platform in order to guide future research directions. " +
        "In the next part of the project, Ciencia Research will work with the J&J team and develop an abstract to " +
        "be presented in a scientific congress. It is a requirement to disseminate the findings since the use of " +
        "the dataset is approved for research purpose only by the Swedish Ethics Review Authority.</p>" +
        "<br/>" +
        "<p>Almost a quarter of all newly diagnosed cases of mCRC present with de novo metastatic disease.1,2 This " +
        "population exhibits a unique clinical profile as patients have not yet undergone treatment and may present " +
        "distinctive biological or molecular features compared to cancers that metastasized later. The current " +
        "analysis focuses on patients with de novo mCRC (M1 stage at diagnosis) between 2005 and 2023. Data " +
        "visualization is divided into 4 parts: (1) Epidemiology and Baseline Characteristics, (2) Treatment " +
        "Pattern, (3) Overall Survival, and (4) Healthcare Resource Utilization. </p><br/>" +
        "<br/>" +
        "<p>This analysis included a total of 24,048 patients with de novo mCRC identified between 2005 and 2023.<br/>" +
        "The Epidemiology and Baseline Characteristics section presents statistics on incidence, prevalence, and " +
        "case mortality. Baseline characteristics including age, sex, and Charlson comorbidity index are " +
        "provided.<br/>" +
        "The Treatment Pattern section describes the proportion of patients receiving cancer treatment by type, " +
        "including surgery, chemotherapy, and radiotherapy. An exploratory analysis is included to visualize the " +
        "sequence of treatment received among mCRC patients by therapy type.<br/>" +
        "In the Overall Survival section, survival rates and median survival time are presented together with an " +
        "interactive Kaplan-Meier plot.  that allows specific patient subgroups (by age and sex) to be " +
        "highlighted.<br/>" +
        "Finally, the Healthcare Resource Utilization section shows the medical burden on patients with mCRC " +
        "through the time spent in hospital care for different causes categorized by ICD-10 chapters. Both " +
        "aggregated and individual values are provided in the results.</p>" +
        "<br/>" +
        "<p>In summary, this interactive panel provides an overview of the de novo mCRC population in Sweden in " +
        "real-world and lays the foundation of future studies on this patient population.</p>" +
        "<br/>" +
        "<h3>Data Sources</h3>" +
        "<br/>" +
        "<p>The Ciencia Research Colorectal Cancer Dataset (CRCD) used to generate real-world insights in this " +
        "project consists of granular data from multiple population-based registers, including the Swedish Cancer " +
        "Register, Swedish National Patient Register, Swedish Prescribed Drug Register, and Swedish Cause of Death " +
        "Register. Owing to the universal healthcare system, all residents in Sweden are given a unique personal " +
        "identification number (PIN) and automatically enrolled in these national registers when they seek " +
        "healthcare services at secondary care units. Data across the registers are linked via the patients’ " +
        "unique PIN and therefore allows a comprehensive characterization of the patient population.</p>" +
        "<br/>" +
        "<p>In Sweden, all healthcare providers are required by law to report newly diagnosed cancer cases to the " +
        "Swedish Cancer Register. Regionalized data collection is consolidated every year, ensuring accurate and " +
        "robust registration of cancer cases at the national level. The Swedish Cancer Register captures data on " +
        "cancer diagnosis (date and ICD-10 diagnosis code), tumor site, histological type, and TNM staging. " +
        "However, information on treatment and follow-up data, such as disease progression or treatment response " +
        "are not reported to this register.<br/>" +
        "The Swedish Patient Register collects detailed data on all inpatient and outpatient visits at secondary " +
        "care units nationwide, including admission and discharge dates, as well as diagnoses (ICD-10) and " +
        "procedure codes associated with each visit. In-hospital medication is also reported by ATC codes, but is " +
        "often incomplete and is therefore not recommended as a stand-alone source for inferring treatment " +
        "patterns.<br/>" +
        "The Swedish Prescribed Drug Register contains data on all prescribed drug dispensed by pharmacies in " +
        "Sweden, including detailed information on the medication (name and ATC code), date of dispensation, " +
        "dosage, and quantity.<br/>" +
        "The Swedish Cause of Death Register records death information, including the date and ICD-10-coded cause, " +
        "for all individuals residing in Sweden.</p>" +
        "<br/>" +
        "<h3>References</h3>" +
        "<br/>" +
        "<p>" +
        "1. Siegel RL et al. Cancer statistics, 2023. CA Cancer J Clin. 2023;73(1):17-48.<br/>" +
        "2. Loroña NC et al. de novo metastases in patients with primary colorectal cancer: a surveillance, " +
        "epidemiology, and end results analysis. Cancer Causes Control. 2025,36(9):937-946." +
        "</p>"
  })
});
</script>
